import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  CompareArrows as CompareIcon,
  GridView as GridViewIcon,
} from '@mui/icons-material';
import TickerSelect from './TickerSelect';
import ComparisonCharts from './ComparisonCharts';
import AggregatedAnalysis from './AggregatedAnalysis';
import DividendUpload from './DividendUpload';
import { REITCompareResponse } from '../types';

const TIME_RANGES = [
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '6M', days: 180 },
  { label: '1Y', days: 365 },
  { label: '2Y', days: 730 },
  { label: 'ALL', days: 0 },
];

const GRANULARITIES = [
  { label: '30m', value: '30min' },
  { label: '1H', value: '1hour' },
  { label: '1D', value: '1day' },
];

export type Granularity = '30min' | '1hour' | '1day';

// REIT parent tickers and their preferred stock mappings
const REIT_PREFERREDS: Record<string, string[]> = {
  AGNC: ['AGNCP', 'AGNCL', 'AGNCM', 'AGNCN', 'AGNCO'],
  NLY: ['NLY PRF', 'NLY PRG', 'NLY PRI'],
  FBRT: ['FBRT PRE'],
  DX: ['DX PRC'],
  TWO: ['TWO PRA', 'TWO PRB', 'TWO PRC'],
  RITM: ['RITM PRA', 'RITM PRB', 'RITM PRC', 'RITM PRD'],
  MFA: ['MFA PRB', 'MFA PRC'],
  IVR: ['IVR PRB', 'IVR PRC'],
  MITT: ['MITT PRA', 'MITT PRB', 'MITT PRC'],
  CIM: ['CIM PRA', 'CIM PRB', 'CIM PRC', 'CIM PRD'],
  PMT: ['PMT PRA', 'PMT PRB', 'PMT PRC'],
  EFC: ['EFC PRA', 'EFC PRB', 'EFC PRC', 'EFC PRD', 'EFC PRE'],
  ARR: ['ARR PRC'],
  CHMI: ['CHMI PRA', 'CHMI PRB'],
  NYMT: ['NYMTL', 'NYMTM', 'NYMTN', 'NYMTZ'],
  RC: ['RC PRC', 'RC PRE'],
};

export type PriceMethod = 'dirty' | 'add_dividend' | 'remove_accrual';

export default function Dashboard() {
  // Main tab state
  const [mainTab, setMainTab] = useState<'comparison' | 'aggregated'>('comparison');

  // Shared controls state
  const [selectedDays, setSelectedDays] = useState<number>(365);
  const [priceMethod, setPriceMethod] = useState<PriceMethod>('dirty');
  const [granularity, setGranularity] = useState<Granularity>('1day');

  // Comparison tab state
  const [reitTickers, setReitTickers] = useState<string[]>([]);
  const [selectedTicker1, setSelectedTicker1] = useState<string>('');
  const [selectedTicker2, setSelectedTicker2] = useState<string>('');
  const [selectedPreferred1, setSelectedPreferred1] = useState<string | null>(null);
  const [selectedPreferred2, setSelectedPreferred2] = useState<string | null>(null);
  const [compareData, setCompareData] = useState<REITCompareResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [dividendDialogOpen, setDividendDialogOpen] = useState<boolean>(false);

  // Get preferred tickers for a parent ticker
  const getPreferreds = (parentTicker: string): string[] => {
    return REIT_PREFERREDS[parentTicker] || [];
  };

  // Fetch REIT tickers on mount
  useEffect(() => {
    fetch('/api/reits')
      .then((res) => res.json())
      .then((data) => {
        // API returns { tickers: [...] } where tickers are the parent REIT tickers
        const tickers = Array.isArray(data.tickers) ? data.tickers : Object.keys(data.tickers || {});
        setReitTickers(tickers);
      })
      .catch((err) => console.error('Failed to fetch REITs:', err));
  }, []);

  // Compare function
  const fetchComparison = useCallback(async () => {
    if (!selectedTicker1 || !selectedTicker2) return;

    setLoading(true);
    setError(null);

    try {
      // Determine which tickers to use for prices/dividends/accruals
      const priceTicker1 = selectedPreferred1 || selectedTicker1;
      const priceTicker2 = selectedPreferred2 || selectedTicker2;

      const res = await fetch(
        `/api/compare-reits?ticker1=${encodeURIComponent(selectedTicker1)}&ticker2=${encodeURIComponent(selectedTicker2)}&priceTicker1=${encodeURIComponent(priceTicker1)}&priceTicker2=${encodeURIComponent(priceTicker2)}&days=${selectedDays}&method=${priceMethod}&granularity=${granularity}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch comparison data');
      }

      const data: REITCompareResponse = await res.json();
      setCompareData(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [selectedTicker1, selectedTicker2, selectedPreferred1, selectedPreferred2, selectedDays, priceMethod, granularity]);

  // Auto-fetch when tickers or days change
  useEffect(() => {
    if (selectedTicker1 && selectedTicker2) {
      fetchComparison();
    }
  }, [selectedTicker1, selectedTicker2, selectedPreferred1, selectedPreferred2, selectedDays, priceMethod, granularity, fetchComparison]);

  return (
    <Box sx={{ p: 3, maxWidth: 1800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center', position: 'relative' }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <TrendingUpIcon sx={{ fontSize: 32 }} />
          REITs Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Compare mortgage REITs: premium vs. discount
        </Typography>
        {/* Settings Button */}
        <Tooltip title="Manage Dividend Data">
          <IconButton
            onClick={() => setDividendDialogOpen(true)}
            sx={{ position: 'absolute', top: 0, left: 16 }}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Dividend Upload Dialog */}
      <DividendUpload
        open={dividendDialogOpen}
        onImportSuccess={fetchComparison}
        onClose={() => setDividendDialogOpen(false)}
      />

      {/* Shared Controls: Tabs + Time Range + Method + Granularity */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          {/* Main Tabs */}
          <Tabs
            value={mainTab}
            onChange={(_, value) => setMainTab(value)}
            centered
          >
            <Tab
              icon={<CompareIcon />}
              iconPosition="start"
              label="One vs One"
              value="comparison"
            />
            <Tab
              icon={<GridViewIcon />}
              iconPosition="start"
              label="One vs All"
              value="aggregated"
            />
          </Tabs>

          {/* Shared Controls Row */}
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" flexWrap="wrap">
            {/* Time Range */}
            <ButtonGroup variant="outlined" size="small">
              {TIME_RANGES.map((range) => (
                <Button
                  key={range.days}
                  onClick={() => setSelectedDays(range.days)}
                  variant={selectedDays === range.days ? 'contained' : 'outlined'}
                >
                  {range.label}
                </Button>
              ))}
            </ButtonGroup>

            {/* Price Method */}
            <ToggleButtonGroup
              value={priceMethod}
              exclusive
              onChange={(_, value) => value && setPriceMethod(value)}
              size="small"
            >
              <ToggleButton value="dirty">
                <Tooltip title="Use raw market prices">
                  <span>Dirty Price</span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="add_dividend">
                <Tooltip title="Add unaccrued portion of dividend (smooth adjustment)">
                  <span>+ Dividend</span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="remove_accrual">
                <Tooltip title="Subtract cumulative accrual from price and book value">
                  <span>- Accrual</span>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Granularity */}
            <ToggleButtonGroup
              value={granularity}
              exclusive
              onChange={(_, value) => value && setGranularity(value)}
              size="small"
            >
              {GRANULARITIES.map((g) => (
                <ToggleButton key={g.value} value={g.value}>
                  <Tooltip title={`${g.label} candles`}>
                    <span>{g.label}</span>
                  </Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {/* Loading indicator */}
            {loading && <CircularProgress size={20} />}
          </Stack>
        </Stack>
      </Paper>

      {/* Tab Content */}
      {mainTab === 'comparison' ? (
      /* Comparison Tab: Left Selector | Charts | Right Selector */
      <Grid container spacing={3}>
        {/* Left Ticker Selector */}
        <Grid item xs={12} md={2}>
          <Paper sx={{ p: 2, height: '100%', borderTop: '3px solid #4ecdc4' }}>
            <Typography variant="subtitle2" sx={{ color: '#4ecdc4', mb: 2, fontWeight: 600 }}>
              TICKER 1
            </Typography>
            <TickerSelect
              tickers={reitTickers}
              selectedTicker={selectedTicker1}
              onTickerChange={setSelectedTicker1}
              preferredTickers={getPreferreds(selectedTicker1)}
              selectedPreferred={selectedPreferred1}
              onPreferredChange={setSelectedPreferred1}
              color="#4ecdc4"
            />
          </Paper>
        </Grid>

        {/* Center: Charts */}
        <Grid item xs={12} md={8}>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Charts */}
          {compareData ? (
            <ComparisonCharts data={compareData} priceMethod={priceMethod} />
          ) : (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {selectedTicker1 && selectedTicker2
                  ? 'Loading comparison...'
                  : 'Select tickers on both sides to compare'}
              </Typography>
            </Paper>
          )}
        </Grid>

        {/* Right Ticker Selector */}
        <Grid item xs={12} md={2}>
          <Paper sx={{ p: 2, height: '100%', borderTop: '3px solid #ff6b6b' }}>
            <Typography variant="subtitle2" sx={{ color: '#ff6b6b', mb: 2, fontWeight: 600 }}>
              TICKER 2
            </Typography>
            <TickerSelect
              tickers={reitTickers}
              selectedTicker={selectedTicker2}
              onTickerChange={setSelectedTicker2}
              preferredTickers={getPreferreds(selectedTicker2)}
              selectedPreferred={selectedPreferred2}
              onPreferredChange={setSelectedPreferred2}
              color="#ff6b6b"
            />
          </Paper>
        </Grid>
      </Grid>
      ) : (
      /* Aggregated Analysis Tab */
      <AggregatedAnalysis
        reitTickers={reitTickers}
        selectedDays={selectedDays}
        priceMethod={priceMethod}
        granularity={granularity}
      />
      )}
    </Box>
  );
}
